import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, AlertCircle, CheckCircle, Star } from 'lucide-react';
import { intakeStatusService } from '../../services/api';
import ConfirmDialog from '../common/ConfirmDialog';

const IntakeStatusManager = () => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newName, setNewName] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', message: '', onConfirm: null, variant: 'warning', confirmText: 'Confirm' });

  useEffect(() => {
    loadStatuses();
  }, []);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const loadStatuses = async () => {
    try {
      setLoading(true);
      const response = await intakeStatusService.getIntakeStatuses();
      setStatuses(response.data || []);
    } catch (err) {
      console.error('Error loading intake statuses:', err);
      setMessage({ type: 'error', text: 'Failed to load intake statuses' });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      await intakeStatusService.createIntakeStatus({
        name: newName.trim(),
        display_order: statuses.length,
        is_default: statuses.length === 0
      });
      setNewName('');
      await loadStatuses();
      setMessage({ type: 'success', text: 'Intake status added' });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to add status' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Intake Status',
      message: 'Delete this intake status? Clients using it will have their sub-status cleared.',
      variant: 'danger',
      confirmText: 'Delete',
      onConfirm: async () => {
        try {
          await intakeStatusService.deleteIntakeStatus(id);
          await loadStatuses();
          setMessage({ type: 'success', text: 'Intake status deleted' });
        } catch (err) {
          setMessage({ type: 'error', text: 'Failed to delete status' });
        }
      },
    });
  };

  const handleToggleDefault = async (id) => {
    setSaving(true);
    try {
      await intakeStatusService.updateIntakeStatus(id, { is_default: true });
      await loadStatuses();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update default' });
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (index, direction) => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= statuses.length) return;

    setSaving(true);
    try {
      const current = statuses[index];
      const swap = statuses[swapIndex];
      await Promise.all([
        intakeStatusService.updateIntakeStatus(current.id, { display_order: swap.display_order }),
        intakeStatusService.updateIntakeStatus(swap.id, { display_order: current.display_order })
      ]);
      await loadStatuses();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to reorder' });
    } finally {
      setSaving(false);
    }
  };

  const handleRename = async (id, newNameValue) => {
    if (!newNameValue.trim()) return;
    try {
      await intakeStatusService.updateIntakeStatus(id, { name: newNameValue.trim() });
      await loadStatuses();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to rename' });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Intake Statuses</h2>
      <p className="text-sm text-gray-600 mb-6">
        Define the stages of your intake pipeline. These sub-statuses help track where clients
        are in the intake process (e.g. "New Lead", "Consultation Scheduled", "Documents Pending").
      </p>

      {/* Message */}
      {message.text && (
        <div className={`mb-4 p-3 rounded-lg flex items-center ${
          message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="h-4 w-4 mr-2" />
          ) : (
            <AlertCircle className="h-4 w-4 mr-2" />
          )}
          {message.text}
        </div>
      )}

      {/* Add new status */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          placeholder="New intake status name..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-yellow-500"
        />
        <button
          onClick={handleAdd}
          disabled={!newName.trim() || saving}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg flex items-center hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4 mr-1" />
          Add
        </button>
      </div>

      {/* Status list */}
      {statuses.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No intake statuses defined yet.</p>
          <p className="text-sm mt-1">Add your first status above to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {statuses.map((status, index) => (
            <div
              key={status.id}
              className="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-3 bg-gray-50"
            >
              {/* Reorder buttons */}
              <div className="flex flex-col">
                <button
                  onClick={() => handleReorder(index, 'up')}
                  disabled={index === 0 || saving}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleReorder(index, 'down')}
                  disabled={index === statuses.length - 1 || saving}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>

              {/* Name (editable on blur) */}
              <input
                type="text"
                defaultValue={status.name}
                onBlur={(e) => {
                  if (e.target.value !== status.name) {
                    handleRename(status.id, e.target.value);
                  }
                }}
                className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 font-medium"
              />

              {/* Default toggle */}
              <button
                onClick={() => handleToggleDefault(status.id)}
                disabled={saving}
                title={status.is_default ? 'Default status' : 'Set as default'}
                className={`p-1 rounded ${
                  status.is_default
                    ? 'text-yellow-500'
                    : 'text-gray-300 hover:text-yellow-400'
                }`}
              >
                <Star className={`h-4 w-4 ${status.is_default ? 'fill-yellow-500' : ''}`} />
              </button>

              {/* Delete */}
              <button
                onClick={() => handleDelete(status.id)}
                className="text-gray-400 hover:text-red-500 p-1"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-gray-400 mt-4">
        The starred status will be automatically assigned to new intake clients.
        Deleting a status will clear it from any clients currently using it.
      </p>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.confirmText}
      />
    </div>
  );
};

export default IntakeStatusManager;
